/**
 * Client for the local POS bridge.
 *
 * The bridge runs on the cashier's own machine because the terminal is only
 * reachable over ws:// on the office network, which a page served over HTTPS
 * cannot open. http://localhost is a trustworthy origin, so calling the bridge
 * from here is allowed; Chrome adds a Private Network Access preflight, which
 * the bridge answers.
 *
 * Plain fetch on purpose: the shared axios client logs the user out on 403, and
 * a bridge auth failure has nothing to do with their admin session.
 */
import axios from "axiosClient";

const URL_KEY = "posBridgeUrl";
const TOKEN_KEY = "posBridgeToken";

export const DEFAULT_BRIDGE_URL = "http://localhost:9123";

// A local cache of the settings: bridge calls read it synchronously. The copy
// that matters is on the server (see loadBridgeSettings).
export const getBridgeSettings = () => ({
  url: localStorage.getItem(URL_KEY) || DEFAULT_BRIDGE_URL,
  token: localStorage.getItem(TOKEN_KEY) || "",
});

export const saveBridgeSettings = ({ url, token }) => {
  localStorage.setItem(URL_KEY, String(url || "").replace(/\/+$/, ""));
  localStorage.setItem(TOKEN_KEY, String(token || ""));
};

/**
 * Fetches this account's bridge settings from the server and caches them here,
 * so the till reconnects by itself after signing out, clearing the browser or
 * restarting the laptop.
 *
 * With `adoptLocal`, a token that exists only in this browser - saved before
 * settings moved to the server - is uploaded to the account once, so nobody has
 * to find and retype it.
 *
 * Only call this for users with POS access: the endpoint answers 403 otherwise,
 * and the shared axios client treats any 403 as a sign-out.
 */
export const loadBridgeSettings = async ({ adoptLocal = false } = {}) => {
  let data;
  try {
    ({ data } = await axios.get("/api/pos/bridge-settings"));
  } catch {
    return null; // keep working from the cache
  }

  if (data?.effective?.token) {
    saveBridgeSettings(data.effective);
    return data;
  }

  const local = getBridgeSettings();
  if (adoptLocal && local.token) {
    try {
      await axios.put("/api/pos/bridge-settings", { ...local, scope: "user" });
      return (await axios.get("/api/pos/bridge-settings")).data;
    } catch {
      /* the cached token still works for this session */
    }
  }
  return data;
};

/** Saves to the account (or, for an admin, as everyone's default). */
export const storeBridgeSettings = async ({ url, token, scope = "user" }) => {
  await axios.put("/api/pos/bridge-settings", { url, token, scope });
  return loadBridgeSettings();
};

/** Drops an account's own settings so it falls back to the shared default. */
export const clearStoredBridgeSettings = async (scope = "user") => {
  await axios.put("/api/pos/bridge-settings", { scope, clear: true });
  return loadBridgeSettings();
};

export class BridgeError extends Error {
  constructor(message, { code, status, retryableWithSameId } = {}) {
    super(message);
    this.name = "BridgeError";
    this.code = code;
    this.status = status;
    // true when the command may or may not have reached the terminal
    this.retryableWithSameId = Boolean(retryableWithSameId);
  }
}

const request = async (path, { method = "GET", body, timeoutMs = 200000 } = {}) => {
  const { url, token } = getBridgeSettings();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${url}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "x-bridge-token": token } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (error) {
    throw new BridgeError(
      error.name === "AbortError"
        ? "The bridge did not answer in time."
        : "Cannot reach the POS bridge. Is it running on this computer?",
      { code: "BridgeUnreachable" },
    );
  } finally {
    clearTimeout(timer);
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401) {
      throw new BridgeError("The bridge rejected this token. Check POS settings.", {
        code: "InvalidBridgeToken",
        status: 401,
      });
    }
    if (response.status === 403) {
      throw new BridgeError(
        "The bridge is not configured to accept calls from this address.",
        { code: "OriginNotAllowed", status: 403 },
      );
    }
    throw new BridgeError(data?.detail || data?.error || "The bridge returned an error.", {
      code: data?.error,
      status: response.status,
      retryableWithSameId: data?.retryableWithSameId,
    });
  }

  return data;
};

export const bridgeHealth = () => request("/health");
export const bridgeStatus = () => request("/status");
export const setTerminalAddress = (host, port) =>
  request("/terminal", { method: "POST", body: { host, port: Number(port) } });
export const reconnectTerminal = () => request("/reconnect", { method: "POST" });

/** Sends one command. `id` is the caller's, so a retry cannot double-register. */
export const sendCommand = ({ id, command, data, timeoutMs }) =>
  request("/command", { method: "POST", body: { id, command, data }, timeoutMs });

/** Sends a rasterised receipt to the till's printer. */
export const printReceipt = ({ width, height, data }) =>
  request("/print", {
    method: "POST",
    body: { width, height, data },
    timeoutMs: 30000,
  });

export const bridgeJournal = (pendingOnly = false) =>
  request(`/journal?limit=200${pendingOnly ? "&pending=1" : ""}`);
export const ackJournalEntry = (id) =>
  request(`/journal/${encodeURIComponent(id)}/ack`, { method: "POST" });
